import React, { useEffect, useId, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './VehicleManagementDrawer.module.css';
import { useModal } from '../../store/ModalProvider';
import type {
  DriversVehiclesAvailabilityModalData,
  VehicleServiceTotalsModalData,
  VehicleWashModalData,
} from '../../types/modal';

type VehiclesManagementDrawerProps = {
  open: boolean;
  onClose: () => void;
};

type AccordionSectionProps = {
  id: string;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
  className?: string;
  headerContent?: React.ReactNode;
};

const AccordionSection = ({
  id,
  title,
  isOpen,
  onToggle,
  children,
  className,
  headerContent,
}: AccordionSectionProps) => (
  <div className={`${styles.section} ${className ?? ''}`.trim()}>
    <button
      type="button"
      className={`${styles.buttonReset} ${styles.sectionHeader}`}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`${id}-content`}
      id={`${id}-header`}
    >
      <span className={styles.sectionTitle}>{title}</span>
      <span className={styles.sectionHeaderRight}>
        {headerContent}
        <ExpandLessIcon
          className={`${styles.caret} ${isOpen ? styles.caretOpen : ''}`}
        />
      </span>
    </button>
    <div
      id={`${id}-content`}
      className={`${styles.sectionBody} ${isOpen ? styles.sectionBodyOpen : ''}`}
      role="region"
      aria-labelledby={`${id}-header`}
      aria-hidden={!isOpen}
    >
      {children}
    </div>
  </div>
);

type SelectFieldProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
};

const SelectField = ({ label, value, options, onChange }: SelectFieldProps) => {
  const id = useId();

  return (
    <div className={styles.selectField}>
      <label className={styles.selectLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={styles.selectInput}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className={styles.selectIcon} />
    </div>
  );
};

export const VehiclesManagementDrawer = ({
  open,
  onClose,
}: VehiclesManagementDrawerProps) => {
  const { openModal } = useModal();
  const closeAnimationDurationMs = 400;
  const [isVisible, setIsVisible] = useState(open);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(true);

  const vehicleDefaultFilter = 'Todas';
  const availabilityDefaultFilter = 'Todos';
  const typeDefaultFilter = 'Todas';
  const capacityDefaultFilter = 'Todas';
  const zoneDefaultFilter = 'Todas';

  const vehicleOptions = ['Todas', '0046', '0062', '0129'];
  const availabilityOptions = ['Todos', 'Escalada', 'Nao Escalada', 'Disponivel'];
  const typeOptions = ['Todas', 'Turismo', 'Expresso', 'MiniBus'];
  const capacityOptions = ['Todas', '20', '40', '55'];
  const zoneOptions = ['Todas', 'Norte', 'Centro', 'Sul'];

  const [applyDefaultFilters, setApplyDefaultFilters] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState(vehicleDefaultFilter);
  const [availabilityFilter, setAvailabilityFilter] =
    useState(availabilityDefaultFilter);
  const [typeFilter, setTypeFilter] = useState(typeDefaultFilter);
  const [capacityFilter, setCapacityFilter] = useState(capacityDefaultFilter);
  const [zoneFilter, setZoneFilter] = useState(zoneDefaultFilter);

  const filterSectionId = useId();
  const consultSectionId = useId();
  const sendSectionId = useId();
  const printSectionId = useId();
  const exportSectionId = useId();

  const consultItems = [
    'Mapa de Viaturas',
    'Viaturas e Motoristas Disponíveis',
    'Serviços e Totais por Viatura',
  ];
  const sendItems = ['Lavagem de Viaturas'];
  const printItems = ['Serviços e Totais por Viatura', 'Lavagem de Viaturas'];
  const exportItems = ['Serviços e Totais por Viatura'];
  const driversVehiclesAvailabilityLabel = 'Viaturas e Motoristas Disponíveis';
  const vehicleServiceTotalsLabel = 'Serviços e Totais por Viatura';
  const vehicleWashLabel = 'Lavagem de Viaturas';

  const filtersApplied = useMemo(() => {
    let count = 0;
    if (vehicleFilter !== vehicleDefaultFilter) {
      count += 1;
    }
    if (availabilityFilter !== availabilityDefaultFilter) {
      count += 1;
    }
    if (typeFilter !== typeDefaultFilter) {
      count += 1;
    }
    if (capacityFilter !== capacityDefaultFilter) {
      count += 1;
    }
    if (zoneFilter !== zoneDefaultFilter) {
      count += 1;
    }
    return count;
  }, [
    availabilityDefaultFilter,
    availabilityFilter,
    capacityDefaultFilter,
    capacityFilter,
    typeDefaultFilter,
    typeFilter,
    vehicleDefaultFilter,
    vehicleFilter,
    zoneDefaultFilter,
    zoneFilter,
  ]);

  const handleClearFilters = () => {
    setVehicleFilter(vehicleDefaultFilter);
    setAvailabilityFilter(availabilityDefaultFilter);
    setTypeFilter(typeDefaultFilter);
    setCapacityFilter(capacityDefaultFilter);
    setZoneFilter(zoneDefaultFilter);
  };

  const openVehicleServiceTotalsModal = () => {
    const modalData: VehicleServiceTotalsModalData = {
      vehicleId: 1,
      startDate: '02/05/2024',
      endDate: '08/05/2024',
      filial: 'Transol',
      vehicle: '0046',
      includeScheduleTime: true,
      services: [
        {
          id: '1',
          requisicao: '95509',
          motorista: 'Alexandre Gomes',
          numMec: '0137',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          localInicio: 'Lisboa',
          localTermino: 'Torres Vedras',
          horaTermino: '10:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: '2',
          requisicao: '84211',
          motorista: 'Beatriz Silva',
          numMec: '0298',
          dataInicio: '03/05/2024',
          horaInicio: '14:00',
          localInicio: 'Lisboa',
          localTermino: 'Almada',
          horaTermino: '15:00',
          dataTermino: '03/05/2024',
          cliente: 'Scenic Tours',
        },
        {
          id: '3',
          requisicao: '84212',
          motorista: 'Carlos Cardoso',
          numMec: '0123',
          dataInicio: '03/05/2024',
          horaInicio: '09:00',
          localInicio: 'Braga',
          localTermino: 'Guimarães',
          horaTermino: '10:30',
          dataTermino: '03/05/2024',
          cliente: 'Cultural Walks',
        },
        {
          id: '4',
          requisicao: '84213',
          motorista: 'Ana Almeida',
          numMec: '0211',
          dataInicio: '04/05/2024',
          horaInicio: '16:00',
          localInicio: 'Coimbra',
          localTermino: 'Figueira da Foz',
          horaTermino: '17:30',
          dataTermino: '04/05/2024',
          cliente: 'Sunset Escapes',
        },
      ],
      totals: [
        {
          id: '1',
          tipo: 'Agências Viagens',
          numero: '5',
          valor: '405,90',
          kmsCarga: '750',
          kmsVazio: '50',
          totalKms: '800',
        },
        {
          id: '2',
          tipo: 'Ocasional',
          numero: '10',
          valor: '525,00',
          kmsCarga: '1140',
          kmsVazio: '196',
          totalKms: '1336',
        },
        {
          id: '3',
          tipo: 'Totais',
          numero: '15',
          valor: '930,90',
          kmsCarga: '1890',
          kmsVazio: '246',
          totalKms: '2136',
        },
      ],
      stats: {
        kmsCarga: '1890',
        kmsVazio: '246',
        totalKms: '2136',
        duracao: '22:30',
      },
    };

    openModal('Servicos e Totais por Viatura', modalData, []);
  };

  const handleConsultItemClick = (item: string) => {
    if (item === driversVehiclesAvailabilityLabel) {
      const modalData: DriversVehiclesAvailabilityModalData = {
        startDate: '02/05/2024 13:15',
        endDate: '03/05/2024 16:15',
        empresa: 'Transol',
        filial: 'Transol',
        lotacaoOperador: 'Superior ou Igual a',
        lotacao: '40',
        anoViatura: 'Posterior a 2020',
        drivers: [
          {
            id: 'driver-1',
            motorista: 'Beatriz Silva',
            numMec: '298',
            ultimoServicoInicio: '02/05/2024 15:00',
            ultimoServicoFim: '02/05/2024 16:00',
            origem: 'Faro',
            destino: 'Portimão',
            numFrota: '0062',
            telefone: '910000000',
            inicioPosterior: '02/05/2024 17:00',
            fim: '',
          },
          {
            id: 'driver-2',
            motorista: 'Carlos Cardoso',
            numMec: '123',
            ultimoServicoInicio: '02/05/2024 15:00',
            ultimoServicoFim: '02/05/2024 16:00',
            origem: 'Faro',
            destino: 'Portimão',
            numFrota: '0062',
            telefone: '910000000',
            inicioPosterior: '02/05/2024 17:00',
            fim: '',
          },
        ],
        vehicles: [
          {
            id: 'vehicle-1',
            numFrota: '0062',
            lotacao: '55',
            ano: '2021',
            ultimoServicoInicio: '02/05/2024 15:00',
            ultimoServicoFim: '02/05/2024 16:00',
            origem: 'Faro',
            destino: 'Portimão',
            numMec: '199',
            motorista: 'Anabela Ramos',
            telefone: '910000000',
            folga: '',
          },
          {
            id: 'vehicle-2',
            numFrota: '0021',
            lotacao: '40',
            ano: '2021',
            ultimoServicoInicio: '02/05/2024 15:00',
            ultimoServicoFim: '02/05/2024 16:00',
            origem: 'Faro',
            destino: 'Portimão',
            numMec: '232',
            motorista: 'Miguel Marques',
            telefone: '910000000',
            folga: '',
          },
        ],
      };

      openModal('Disponibilidade de Motoristas e Viaturas', modalData, []);
      return;
    }

    if (item === vehicleServiceTotalsLabel) {
      openVehicleServiceTotalsModal();
    }
  };

  const openVehicleWashModal = () => {
    const modalData: VehicleWashModalData = {
      date: '02/05/2024',
      overnightLocation: 'Todos',
      rows: Array.from({ length: 13 }, (_val, index) => ({
        id: `wash-${index + 1}`,
        viatura: '0071',
        ultimoServico: 'sex 02/05/2024 17:30',
        origemDestino: 'Lisboa Porto',
        diaSeguinte: 'Não',
        primeiraSaida: 'dom 12/05/2024 10:00',
        ultimaLavagem: '',
        comentarios: '',
        pernoita: 'Portimão (Oficina)',
        ultimaAspiracao: '',
      })),
      totalVehicles: 50,
      rowsPerPage: 10,
    };

    openModal('Lavagem de Viaturas', modalData, []);
  };

  const handleSendItemClick = (item: string) => {
    if (item === vehicleWashLabel) {
      openVehicleWashModal();
    }
  };

  const handlePrintItemClick = (item: string) => {
    if (item === vehicleWashLabel) {
      openVehicleWashModal();
      return;
    }

    if (item === vehicleServiceTotalsLabel) {
      openVehicleServiceTotalsModal();
    }
  };

  const handleExportItemClick = (item: string) => {
    if (item !== vehicleServiceTotalsLabel) {
      return;
    }

    const modalData: VehicleServiceTotalsModalData = {
      vehicleId: 1,
      startDate: '02/05/2024',
      endDate: '02/05/2024',
      filial: 'Transol',
      vehicle: '0003 - 78-XA-03',
      includeScheduleTime: true,
      footerVariant: 'export',
      services: [
        {
          id: '1',
          requisicao: '95509',
          motorista: 'Cavaquinho',
          numMec: '0118',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          localInicio: 'Lisboa',
          localTermino: 'Torres Vedras',
          horaTermino: '10:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: '2',
          requisicao: '95509',
          motorista: 'Cavaquinho',
          numMec: '0118',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          localInicio: 'Lisboa',
          localTermino: 'Torres Vedras',
          horaTermino: '10:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
        },
      ],
      totals: [
        {
          id: '1',
          tipo: 'Agências Viagens',
          numero: '2',
          valor: '90,85',
          kmsCarga: '45',
          kmsVazio: '0',
          totalKms: '45',
        },
        {
          id: '2',
          tipo: 'Totais',
          numero: '2',
          valor: '90,85',
          kmsCarga: '45',
          kmsVazio: '0',
          totalKms: '45',
        },
      ],
      stats: {
        kmsCarga: '200',
        kmsVazio: '50',
        totalKms: '250',
        duracao: '02:00',
      },
    };

    openModal('Servicos e Totais por Viatura', modalData, []);
  };

  useEffect(() => {
    let timeoutId: number | undefined;
    let frameId: number | undefined;
    let frameId2: number | undefined;

    if (open) {
      setIsVisible(true);
      setFiltersOpen(false);
      setConsultOpen(false);
      setSendOpen(false);
      setPrintOpen(false);
      setExportOpen(true);
      setIsAnimatingOpen(false);
      if (typeof window === 'undefined') {
        setIsAnimatingOpen(true);
        return undefined;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId2 = window.requestAnimationFrame(() => {
          setIsAnimatingOpen(true);
        });
      });
      return () => {
        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }
        if (frameId2 !== undefined) {
          window.cancelAnimationFrame(frameId2);
        }
      };
    }

    setIsAnimatingOpen(false);
    timeoutId = window.setTimeout(() => {
      setIsVisible(false);
    }, closeAnimationDurationMs);

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [open, closeAnimationDurationMs]);

  if (!isVisible) {
    return null;
  }

  return (
    <aside
      className={`${styles.drawer} ${styles.drawerLeft} ${isAnimatingOpen ? styles.drawerOpen : styles.drawerClosed}`}
      aria-label="Gerir Viaturas"
      aria-hidden={!open}
    >
      <div className={styles.header}>
        <span className={styles.title}>Gerir Viaturas</span>
        <button
          type="button"
          className={`${styles.buttonReset} ${styles.closeButton}`}
          onClick={onClose}
          aria-label="Fechar painel"
        >
          <CloseRoundedIcon className={styles.closeIcon} />
        </button>
      </div>

      <div className={styles.content}>
        <AccordionSection
          id={filterSectionId}
          title="Filtrar"
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          headerContent={
            <span
              className={styles.filterActions}
              style={{
                opacity: filtersApplied > 0 ? 1 : 0,
                pointerEvents: filtersApplied > 0 ? 'auto' : 'none',
              }}
            >
              <button
                type="button"
                className={`${styles.buttonReset} ${styles.filterClear}`}
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </button>
              <span className={styles.filterBadge}>{filtersApplied}</span>
            </span>
          }
        >
          <div className={styles.checkboxRowRight}>
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={applyDefaultFilters}
                  onChange={(event) =>
                    setApplyDefaultFilters(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Aplicar estes Filtros por defeito"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
          </div>

          <div className={styles.fieldStack}>
            <SelectField
              label="Viatura"
              value={vehicleFilter}
              options={vehicleOptions}
              onChange={setVehicleFilter}
            />
            <SelectField
              label="Situação / Disponibilidade"
              value={availabilityFilter}
              options={availabilityOptions}
              onChange={setAvailabilityFilter}
            />
            <SelectField
              label="Tipologia"
              value={typeFilter}
              options={typeOptions}
              onChange={setTypeFilter}
            />
            <SelectField
              label="Lotação"
              value={capacityFilter}
              options={capacityOptions}
              onChange={setCapacityFilter}
            />
            <SelectField
              label="Zona"
              value={zoneFilter}
              options={zoneOptions}
              onChange={setZoneFilter}
            />
          </div>
        </AccordionSection>

        <AccordionSection
          id={consultSectionId}
          title="Consultar"
          isOpen={consultOpen}
          onToggle={() => setConsultOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {consultItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleConsultItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={sendSectionId}
          title="Enviar"
          isOpen={sendOpen}
          onToggle={() => setSendOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {sendItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleSendItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={printSectionId}
          title="Imprimir"
          isOpen={printOpen}
          onToggle={() => setPrintOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {printItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handlePrintItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={exportSectionId}
          title="Exportar"
          isOpen={exportOpen}
          onToggle={() => setExportOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {exportItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleExportItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>
      </div>
    </aside>
  );
};

export default VehiclesManagementDrawer;
